import React, { useState } from "react";
import { Box, Typography, Card, CardContent, FormControl, InputLabel, Select, MenuItem, FormControlLabel, Switch, Divider, TextField, Chip } from "@mui/material";
import MicIcon from "@mui/icons-material/Mic";
import SettingsIcon from "@mui/icons-material/Settings";
import FilterListIcon from "@mui/icons-material/FilterList";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import CustomButton from "../../components/Button/CustomButton";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";

const Transcriber = () => {
  const [settings, setSettings] = useState({
    provider: "Deepgram",
    model: "nova-2",
    language: "en-US",
    punctuation: true,
    diarization: true,
    profanityFilter: false,
    piiRedaction: true,
    customVocabulary: ["VAPI", "webhook", "API", "CRM"],
  });

  const [newWord, setNewWord] = useState("");

  const handleChange = (field, value) => {
    setSettings({ ...settings, [field]: value });
  };

  const handleAddVocabulary = () => {
    if (newWord.trim()) {
      setSettings({
        ...settings,
        customVocabulary: [...settings.customVocabulary, newWord.trim()],
      });
      setNewWord("");
    }
  };

  const handleRemoveVocabulary = (word) => {
    setSettings({
      ...settings,
      customVocabulary: settings.customVocabulary.filter((w) => w !== word),
    });
  };

  const handleSave = () => {
    alert("Transcriber settings saved successfully!");
  };

  return (
    <Box sx={{ padding: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom fontWeight={600}>
            Transcriber Configuration
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Configure speech-to-text services for accurate call transcription
          </Typography>
        </Box>
        <CustomButton
          icon={<SaveIcon />}
          iconPosition="left"
          text="Save Settings"
          onClick={handleSave}
        />
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Card sx={{ borderRadius: 2, border: "1px solid #E0E0E0" }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <MicIcon sx={{ color: "primary.main" }} />
              <Typography variant="h6" fontWeight={600}>
                Provider & Model Selection
              </Typography>
            </Box>
            <Divider sx={{ mb: 3 }} />

            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Speech-to-Text Provider</InputLabel>
                <Select
                  value={settings.provider}
                  label="Speech-to-Text Provider"
                  onChange={(e) => handleChange("provider", e.target.value)}
                >
                  <MenuItem value="Deepgram">
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        Deepgram
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        High-accuracy AI-powered transcription
                      </Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="AssemblyAI">
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        AssemblyAI
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Advanced speech recognition with speaker diarization
                      </Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="Google">
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        Google Speech-to-Text
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Google Cloud STT with enhanced models
                      </Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="Azure">
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        Azure Speech Services
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Microsoft Azure Cognitive Services STT
                      </Typography>
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>

              {settings.provider === "Deepgram" && (
                <FormControl fullWidth>
                  <InputLabel>Deepgram Model</InputLabel>
                  <Select
                    value={settings.model}
                    label="Deepgram Model"
                    onChange={(e) => handleChange("model", e.target.value)}
                  >
                    <MenuItem value="nova-2">Nova 2 (Most Accurate)</MenuItem>
                    <MenuItem value="nova">Nova (Balanced)</MenuItem>
                    <MenuItem value="base">Base (Fast & Cost-effective)</MenuItem>
                    <MenuItem value="enhanced">Enhanced (General Purpose)</MenuItem>
                  </Select>
                </FormControl>
              )}

              <FormControl fullWidth>
                <InputLabel>Language</InputLabel>
                <Select
                  value={settings.language}
                  label="Language"
                  onChange={(e) => handleChange("language", e.target.value)}
                >
                  <MenuItem value="en-US">English (US)</MenuItem>
                  <MenuItem value="en-GB">English (UK)</MenuItem>
                  <MenuItem value="en-AU">English (AU)</MenuItem>
                  <MenuItem value="es-ES">Spanish (Spain)</MenuItem>
                  <MenuItem value="es-MX">Spanish (Mexico)</MenuItem>
                  <MenuItem value="fr-FR">French (France)</MenuItem>
                  <MenuItem value="de-DE">German (Germany)</MenuItem>
                  <MenuItem value="it-IT">Italian (Italy)</MenuItem>
                  <MenuItem value="pt-BR">Portuguese (Brazil)</MenuItem>
                  <MenuItem value="ja-JP">Japanese (Japan)</MenuItem>
                  <MenuItem value="zh-CN">Chinese (Simplified)</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 2, border: "1px solid #E0E0E0" }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <SettingsIcon sx={{ color: "primary.main" }} />
              <Typography variant="h6" fontWeight={600}>
                Transcription Features
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.punctuation}
                    onChange={(e) => handleChange("punctuation", e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" fontWeight={600}>
                      Smart Punctuation
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Automatically add punctuation and capitalization
                    </Typography>
                  </Box>
                }
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={settings.diarization}
                    onChange={(e) => handleChange("diarization", e.target.checked)}
                    color="success"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" fontWeight={600}>
                      Speaker Diarization
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Identify and separate different speakers in the conversation
                    </Typography>
                  </Box>
                }
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={settings.profanityFilter}
                    onChange={(e) => handleChange("profanityFilter", e.target.checked)}
                    color="warning"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" fontWeight={600}>
                      Profanity Filter
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Automatically filter or redact profanity from transcripts
                    </Typography>
                  </Box>
                }
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={settings.piiRedaction}
                    onChange={(e) => handleChange("piiRedaction", e.target.checked)}
                    color="error"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" fontWeight={600}>
                      PII Redaction
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Automatically redact personally identifiable information (SSN, credit cards, etc.)
                    </Typography>
                  </Box>
                }
              />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 2, border: "1px solid #E0E0E0" }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <FilterListIcon sx={{ color: "primary.main" }} />
              <Typography variant="h6" fontWeight={600}>
                Custom Vocabulary
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />

            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Add industry-specific terms or acronyms to improve transcription accuracy
              </Typography>

              <Box sx={{ display: "flex", gap: 1, mt: 2, mb: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Add custom word or phrase"
                  value={newWord}
                  onChange={(e) => setNewWord(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      handleAddVocabulary();
                    }
                  }}
                />
                <CustomButton
                  icon={<AddIcon />}
                  iconPosition="left"
                  text="Add"
                  onClick={handleAddVocabulary}
                  disabled={!newWord.trim()}
                />
              </Box>

              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 2 }}>
                {settings.customVocabulary.map((word, idx) => (
                  <Chip
                    key={idx}
                    label={word}
                    onDelete={() => handleRemoveVocabulary(word)}
                    color="primary"
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ borderRadius: 2, border: "1px solid #E0E0E0", backgroundColor: "#F9FAFB" }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <VolumeUpIcon sx={{ color: "info.main" }} />
              <Typography variant="h6" fontWeight={600}>
                Current Configuration Summary
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" color="text.secondary">
                  Provider:
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {settings.provider}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" color="text.secondary">
                  Model:
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {settings.model}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" color="text.secondary">
                  Language:
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {settings.language}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" color="text.secondary">
                  Punctuation:
                </Typography>
                <Chip
                  label={settings.punctuation ? "Enabled" : "Disabled"}
                  size="small"
                  color={settings.punctuation ? "success" : "default"}
                  sx={{ height: 20 }}
                />
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" color="text.secondary">
                  Speaker Diarization:
                </Typography>
                <Chip
                  label={settings.diarization ? "Enabled" : "Disabled"}
                  size="small"
                  color={settings.diarization ? "success" : "default"}
                  sx={{ height: 20 }}
                />
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" color="text.secondary">
                  PII Redaction:
                </Typography>
                <Chip
                  label={settings.piiRedaction ? "Enabled" : "Disabled"}
                  size="small"
                  color={settings.piiRedaction ? "success" : "default"}
                  sx={{ height: 20 }}
                />
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2" color="text.secondary">
                  Custom Vocabulary:
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {settings.customVocabulary.length} words
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
          <CustomButton
            icon={<SaveIcon />}
            iconPosition="left"
            text="Save Configuration"
            onClick={handleSave}
            size="large"
          />
        </Box>
      </Box>
    </Box>
  );
};

export default Transcriber;
